import { LoginFlow, UpdateLoginFlowBody } from "@ory/kratos-client";
import { useSetState } from "ahooks";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { FullPageLoader } from "../../layout";
import { getOrInitiateLoginFlow, submitLogin } from "./api";
import { SignInForm } from "./components";

interface LoginFlowState {
  data: LoginFlow | null;
  loading: boolean;
  error: Error | null;
}

export const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get("flow");
  const aal = searchParams.get("aal");
  const returnTo = searchParams.get("return_to");
  const refresh = searchParams.get("refresh");
  const loginChallenge = searchParams.get("login_challenge");

  const [loginFlow, setLoginFlow] = useSetState<LoginFlowState>({
    data: null,
    loading: true,
    error: null,
  });
  const [forceFormRenderKey, setForceFormRender] = useState<number>(0);
  const [errorCode, setErrorCode] = useState<number | null>(null);

  function handleLoginSuccess(returnTo?: string | null) {
    document.location.href = returnTo ?? "/";
  }

  useLayoutEffect(() => {
    const bgClassName = "green-bg";
    document.body.classList.add(bgClassName);
    return function () {
      document.body.classList.remove(bgClassName);
    };
  });

  async function load(
    flowId: string | null,
    aal: string | null,
    returnTo: string | null,
    loginChallenge: string | null
  ): Promise<LoginFlow | null> {
    try {
      const loginFlow = await getOrInitiateLoginFlow(api, {
        ...(flowId && { flowId }),
        ...(aal && { aal }),
        ...(loginChallenge && { loginChallenge }),
        ...(returnTo && { returnTo }),
        refresh: Boolean(refresh ?? loginChallenge),
      });
      if (!loginFlow) {
        // Already logged in, so a login flow could not be created
        handleLoginSuccess();
      }
      return loginFlow;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  useEffect(() => {
    setLoginFlow({
      loading: true,
      error: null,
    });
    load(flowId, aal, returnTo, loginChallenge)
      .then(
        (newLoginFlow: LoginFlow | null) => {
          setLoginFlow({
            data: newLoginFlow,
            error: null,
          });
        },
        (err) => {
          setLoginFlow({
            data: null,
            error: err,
          });
        }
      )
      .finally(() => {
        setLoginFlow({
          loading: false,
        });
        setForceFormRender((prev) => prev + 1);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, aal, returnTo, loginChallenge]);

  async function attemptLogin(values: UpdateLoginFlowBody): Promise<void> {
    if (!loginFlow?.data) {
      // Should not happen
      // Avoid TS compilation errors
      return;
    }

    const [success, newLoginFlow, errorCode] = await submitLogin(
      api,
      loginFlow.data.id,
      values
    );
    if (success) {
      handleLoginSuccess(returnTo);
    }
    if (newLoginFlow) {
      setLoginFlow({ data: newLoginFlow });
      setForceFormRender((prev) => prev + 1);
    }
    if (errorCode) {
      setErrorCode(errorCode);
    }
  }

  if (loginFlow.loading) {
    return <FullPageLoader />;
  }

  if (!loginFlow.data || loginFlow.error) {
    return <div>{loginFlow.error?.message}</div>;
  }

  return (
    <SignInForm
      key={forceFormRenderKey}
      flow={loginFlow.data}
      onAttemptLogin={attemptLogin}
      errorCode={errorCode}
    />
  );
};
